/**
 * Hook principal para fluxo de agendamento
 */

import { useRef, useState } from "react";
import { useToast } from "../../../components/ui/ToastProvider";
import type { BookingFormData, BookingData, PublicService } from "../types";
import { getBookingContactValidationError } from "../utils/bookingValidation";
import { bookingSuccessMessage } from "../utils/bookingSuccessMessage";
import {
  createPublicAppointment,
  resolvePublicBookingSource,
  trackPublicFunnelEvent,
} from "../../../services/public";

interface UseBookingProps {
  slug: string | null;
  authToken?: string | null;
  services: PublicService[];
  multiEnabled: boolean;
  selectedServiceIds: string[];
  selectedServiceId?: string;
  selectedProviderId?: string;
  requireProviderSelection?: boolean;
  date: string;
  selectedTime: string;
  customValues: Record<string, any>;
  products?: Array<{ productId: string; quantity: number }>;
  validateCustomFields: () => string | null;
  onSuccess: () => void;
  /**
   * CNF-04: a unidade manda a confirmação pelo WhatsApp
   * (`whatsappBookingConfirmation` do perfil público). Ausente vale falso.
   */
  whatsappBookingConfirmation?: boolean;
}

export function useBooking({
  slug,
  authToken,
  services,
  multiEnabled,
  selectedServiceIds,
  selectedServiceId,
  selectedProviderId,
  requireProviderSelection = false,
  date,
  selectedTime,
  customValues,
  products = [],
  validateCustomFields,
  onSuccess,
  whatsappBookingConfirmation = false,
}: UseBookingProps) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  // PUB-06: o `loading` só chega ao botão no próximo render. A ref trava o
  // segundo clique que chega antes disso e evitava um POST duplicado.
  const submittingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lastBooking, setLastBooking] = useState<BookingData | null>(null);

  const [suggested, setSuggested] = useState<
    Array<{ time: string; providerId?: string }>
  >([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const getSelectedServiceMetadata = () => {
    const selected = multiEnabled
      ? services.filter((service) => selectedServiceIds.includes(service.id))
      : services.filter((service) => service.id === selectedServiceId);
    const amountCents = selected.reduce((total, service) => {
      if (!service.showPrice || typeof service.price !== "number") return total;
      return total + Math.round(service.price * 100);
    }, 0);
    return {
      amountCents,
      serviceName:
        selected.length === 1
          ? selected[0]?.name
          : selected.length > 1
            ? `${selected.length} serviços selecionados`
            : undefined,
      selectedServiceIds: selected.map((service) => service.id),
    };
  };

  const submitBooking = async (
    formData: BookingFormData,
    providerSlots: Record<string, string[]>,
    providers: any[],
    slotsUnion: string[],
    tokenOverride?: string | null,
    isCurrent: () => boolean = () => true
  ) => {
    if (!slug || !isCurrent()) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      return await submitBookingOnce(
        formData,
        providerSlots,
        providers,
        slotsUnion,
        slug,
        tokenOverride,
        isCurrent
      );
    } finally {
      submittingRef.current = false;
    }
  };

  const submitBookingOnce = async (
    formData: BookingFormData,
    providerSlots: Record<string, string[]>,
    providers: any[],
    slotsUnion: string[],
    slug: string,
    tokenOverride?: string | null,
    isCurrent: () => boolean = () => true
  ) => {
    const hasService = multiEnabled
      ? selectedServiceIds.length > 0
      : Boolean(selectedServiceId);
    if (!hasService) {
      const message = "Selecione um serviço";
      setError(message);
      toast.error(message);
      return;
    }

    if (requireProviderSelection && !selectedProviderId) {
      const message = "Escolha um profissional para continuar.";
      setError(message);
      toast.error(message);
      return;
    }

    if (!date || !selectedTime) {
      const message = "Selecione data e horário";
      setError(message);
      toast.error(message);
      return;
    }

    const contactValidationError = getBookingContactValidationError(formData);
    if (contactValidationError) {
      setError(contactValidationError);
      toast.error(contactValidationError);
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setShowSuggestions(false);
    setSuggested([]);

    const validationError = validateCustomFields();
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }

    setLoading(true);
    const toastId = toast.loading("Confirmando agendamento...");

    try {
      const isMulti = multiEnabled && selectedServiceIds.length > 0;

      const payload: any = {
        ...resolvePublicBookingSource(),
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        whatsapp: formData.whatsapp || undefined,
        phone: formData.phone || undefined,
        notes: formData.notes || undefined,
        date,
        time: selectedTime,
        serviceId: !isMulti ? selectedServiceId || undefined : undefined,
        serviceIds: isMulti ? selectedServiceIds : undefined,
        providerId: selectedProviderId || undefined,
        customFields: Object.keys(customValues).length ? customValues : undefined,
        products: products.length > 0 ? products : undefined,
      };

      const created = await createPublicAppointment(
        slug,
        payload,
        (tokenOverride === undefined ? authToken : tokenOverride) || undefined
      );
      if (!isCurrent()) return false;
      const selectedMetadata = getSelectedServiceMetadata();
      trackPublicFunnelEvent(slug, {
        eventName: "appointment_created",
        serviceId: !isMulti ? selectedServiceId || undefined : selectedServiceIds[0],
        providerId: selectedProviderId || undefined,
        scheduledDate: date,
        scheduledTime: selectedTime,
        metadata: {
          ...selectedMetadata,
          appointmentId: created?.id,
          onlinePayment: false,
        },
      });

      if (isMulti) {
        const chosen = services.filter((s) => selectedServiceIds.includes(s.id));
        const totalDuration = chosen.reduce((acc, s) => acc + s.durationMinutes, 0);
        const totalPrice = chosen.reduce(
          (acc, s) => acc + (s.showPrice && typeof s.price === "number" ? s.price : 0),
          0
        );

        setLastBooking({
          date,
          time: selectedTime,
          serviceName: chosen.map((c) => c.name).join(", "),
          serviceDuration: totalDuration,
          price: totalPrice || null,
          name: formData.name,
          email: formData.email,
          whatsapp: formData.whatsapp,
          notes: formData.notes,
        });
      } else {
        const svc = services.find((s) => s.id === selectedServiceId);
        setLastBooking({
          date,
          time: selectedTime,
          serviceName: svc?.name,
          serviceDuration: svc?.durationMinutes,
          price: typeof svc?.price === "number" ? svc.price : null,
          name: formData.name,
          email: formData.email,
          whatsapp: formData.whatsapp,
          notes: formData.notes,
        });
      }

      const message = bookingSuccessMessage({
        whatsappConfirmation: whatsappBookingConfirmation,
        whatsapp: formData.whatsapp,
      });
      setSuccessMessage(message);
      toast.update(toastId, message, "success");
      setShowSuccessModal(true);
      onSuccess();
      return true;
    } catch (e: any) {
      if (!isCurrent()) return false;
      const status = e?.status;
      const msg = e?.message || "Erro ao agendar";
      setError(msg);
      toast.update(toastId, msg, "error");

      if (status === 409) {
        const suggestions: Array<{ time: string; providerId?: string }> = [];
        const currentProv = selectedProviderId || undefined;

        if (currentProv) {
          const pTimes = providerSlots[currentProv] || [];
          suggestions.push(
            ...pTimes
              .filter((t) => t !== selectedTime)
              .slice(0, 4)
              .map((t) => ({ time: t, providerId: currentProv }))
          );
        }

        if (suggestions.length === 0) {
          const unionTimes = slotsUnion.filter((t) => t !== selectedTime);
          outer: for (const t of unionTimes) {
            for (const p of providers) {
              const pTimes = providerSlots[p.id] || [];
              if (pTimes.includes(t)) {
                suggestions.push({ time: t, providerId: p.id });
                if (suggestions.length >= 4) break outer;
                break;
              }
            }
          }
        }

        setSuggested(suggestions);
        if (suggestions.length > 0) setShowSuggestions(true);
      }
      return false;
    } finally {
      if (!isCurrent()) toast.dismiss(toastId);
      setLoading(false);
    }
  };

  // Novo: fluxo online, só chamado quando pagamento foi aprovado
  const submitBookingAfterPayment = async (payload: any, paymentConfirmation: any) => {
    if (!slug || !selectedTime) return;

    if (requireProviderSelection && !selectedProviderId) {
      const message = "Escolha um profissional para continuar.";
      setError(message);
      toast.error(message);
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setLoading(true);
    const toastId = toast.loading("Confirmando agendamento...");

    try {
      const isMulti = multiEnabled && selectedServiceIds.length > 0;

      const body: any = {
        ...resolvePublicBookingSource(),
        ...payload,
        serviceId: !isMulti ? selectedServiceId || undefined : undefined,
        serviceIds: isMulti ? selectedServiceIds : undefined,
        providerId: selectedProviderId || undefined,
        payment: paymentConfirmation,
        products: products.length > 0 ? products : undefined,
      };

      // Se já existe appointmentId (hold), não cria novo: apenas atualiza estado de sucesso
      if (!body.appointmentId) {
        await createPublicAppointment(slug, body, authToken || undefined);
      }
      const selectedMetadata = getSelectedServiceMetadata();
      trackPublicFunnelEvent(slug, {
        eventName: "appointment_created",
        serviceId: !isMulti ? selectedServiceId || undefined : selectedServiceIds[0],
        providerId: selectedProviderId || undefined,
        scheduledDate: body.date || date,
        scheduledTime: body.time || selectedTime,
        metadata: {
          ...selectedMetadata,
          appointmentId: body.appointmentId || paymentConfirmation?.appointmentId,
          onlinePayment: true,
          reusedHold: Boolean(body.appointmentId),
        },
      });

      if (isMulti) {
        const chosen = services.filter((s) => selectedServiceIds.includes(s.id));
        const totalDuration = chosen.reduce((acc, s) => acc + s.durationMinutes, 0);
        const totalPrice = chosen.reduce(
          (acc, s) => acc + (s.showPrice && typeof s.price === "number" ? s.price : 0),
          0
        );

        setLastBooking({
          date,
          time: selectedTime,
          serviceName: chosen.map((c) => c.name).join(", "),
          serviceDuration: totalDuration,
          price: totalPrice || null,
          name: body.name,
          email: body.email,
          whatsapp: body.whatsapp,
          notes: body.notes,
        });
      } else {
        const svc = services.find((s) => s.id === selectedServiceId);
        setLastBooking({
          date,
          time: selectedTime,
          serviceName: svc?.name,
          serviceDuration: svc?.durationMinutes,
          price: typeof svc?.price === "number" ? svc.price : null,
          name: body.name,
          email: body.email,
          whatsapp: body.whatsapp,
          notes: body.notes,
        });
      }

      const message = bookingSuccessMessage({
        whatsappConfirmation: whatsappBookingConfirmation,
        whatsapp: body.whatsapp,
      });
      setSuccessMessage(message);
      toast.update(toastId, message, "success");
      setShowSuccessModal(true);
      onSuccess();
    } catch (e: any) {
      const msg = e?.message || "Erro ao salvar agendamento após pagamento";
      setError(msg);
      toast.update(toastId, msg, "error");
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    successMessage,
    showSuccessModal,
    lastBooking,
    suggested,
    showSuggestions,
    submitBooking,
    submitBookingAfterPayment, // exposto para o fluxo online
    setShowSuccessModal,
    setSuccessMessage,
    setShowSuggestions,
    setError,
  };
}
